import {booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, Injectable, input, Signal, signal, viewChild} from "@angular/core";
import {BeslutningId, BrukerModell, OppgaveId, OppgaveModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatIcon} from "@angular/material/icon";
import {OppgaveStatusKomponent} from "../oppgave-status/oppgave-status";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {MatIconButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erFavorittHosKallendeBruker} from "felles-ng2/modell/favoritterModellHjelpere";
import {forelderModellFraOppgave, kanOppgaveRedigeres, OppgavestatusInklOverskredet, oppgaveStatusSomSignal} from "felles-ng2/modell/oppgaveModellHjelpere";
import {MatDialog} from "@angular/material/dialog";
import {OppgaveRedigerDialogKomponent} from "../../modale/oppgave-rediger-dialog/oppgave-rediger-dialog";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {OppgaveKommentarerKomponent} from "web-ng2/moduler/oppgave/komponenter/oppgave-kommentarer/oppgave-kommentarer";
import {KommentarerTellerKomponent} from "web-ng2/moduler/kommentar/komponenter/kommentarer-teller/kommentarer-teller";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {scrollDersomNødvendig} from "web-ng2/delt/dom/scrollIntoView";
import {ListeLenkerelasjonerKomponent} from "web-ng2/moduler/lenkrelasjoner/komponenter/liste-lenkerelasjoner/liste-lenkerelasjoner";
import {ForelderstiKomponent} from "web-ng2/delt/komponenter/foreldersti/foreldersti";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";

const debug = initDebugger('OppgaveListevisningKompaktKomponent');

/**
 * Kompakt listevisning av oppgave, for utlisting i sak og beslutning
 */
@Component({
    selector: 'oppgave-listevisning-kompakt',
    templateUrl: 'oppgave-listevisning-kompakt.html',
    styleUrl: 'oppgave-listevisning-kompakt.scss',
    host: {
        '[id]': '"oppg-"+this.oppgave().id',
        '[class]': '"status_"+utvidetStatus()',
        'data-testid': 'oppgave'
    },
    imports: [
        HtmlFormatertKomponent,
        MatIcon,
        OppgaveStatusKomponent,
        AvatarKomponent,
        BekreftKlikkDirektiv,
        MatIconButton,
        MatMenu,
        TranslateModule,
        MatMenuTrigger,
        MatMenuItem,
        OppgaveKommentarerKomponent,
        KommentarerTellerKomponent,
        ListeLenkerelasjonerKomponent,
        ForelderstiKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OppgaveListevisningKompaktKomponent {

    /** css selector uttrykk som kan benyttes for å lokalisere elmenet som kan benyttes i 'scroll til' uttrykk.
     * Merk at vi ikke kan benytte #id direkte da oppgave-listevisning-kompakt elementet ikke er synlig (har display:contents) */
    static scrollTilElementCssUttrykk(oppgaveId: OppgaveId): string {
        return `#oppg-${oppgaveId} > .detaljer > .tittel`;
    }

    /** tittel felt - brukt for å scrolle til ved fokus */
    tittelFelt = viewChild.required<ElementRef<HTMLElement>>('tittelFelt');

    oppgave = input.required<OppgaveModell>();
    visForelder = input(false, {transform: booleanAttribute});

    utvidetStatus: Signal<OppgavestatusInklOverskredet>;
    erVisKommentarer = signal(false);
    redigeringMulig = computed(() => kanOppgaveRedigeres(this.oppgave(), this.kontekst.autentisertBruker()));
    erFavoritt = computed(() => erFavorittHosKallendeBruker(this.oppgave(), this.kontekst.autentisertBruker()));
    forelder = computed(() => forelderModellFraOppgave(this.oppgave()));
    menyDeaktivert = this.navigasjonDeaktivering.navigasjonDeaktivertSomSignal

    constructor(private avhengigheter: OppgaveListevisningKompaktKomponentAvhengigheter,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private matDialog: MatDialog,
                klokke: Klokke,
                destroyRef: DestroyRef) {
        this.utvidetStatus = oppgaveStatusSomSignal(this.oppgave, klokke, destroyRef);
    }

    bruker(): BrukerModell {
        return this.kontekst.autentisertBruker().bruker;
    }

    /** scroller til tittel-feltet og blinker .detaljer-raden */
    fokuser() {
        scrollDersomNødvendig(this.tittelFelt().nativeElement);
        this.tittelFelt().nativeElement.parentElement?.classList.add('ny')
    }

    async inverterUtfoertStatus() {
        await this.avhengigheter.inverterUtførtStatus(this.oppgave());
    }

    async endreFavorittmarkering() {
        await this.avhengigheter.endreFavorittmarkering(this.oppgave());
    }

    rediger() {
        OppgaveRedigerDialogKomponent.vis(this.matDialog, this.oppgave());
    }

    async slett() {
        this.avhengigheter.slett(this.oppgave().id);
    }
}

@Injectable()
export class OppgaveListevisningKompaktKomponentAvhengigheter {

    constructor(private oppgaveDatatjeneste: OppgaveDatatjeneste) {
    }

    async endreFavorittmarkering(oppgave: OppgaveModell) {
        await this.oppgaveDatatjeneste.endreFavorittmarkering(oppgave);
    }

    async slett(id: OppgaveId) {
        await this.oppgaveDatatjeneste.slett(id);
    }

    async inverterUtførtStatus(oppgave: OppgaveModell) {
        await this.oppgaveDatatjeneste.inverterUtførtStatus(oppgave);
    }
}
